/**
 * use-supabase-search.ts — Search hook with sanitization
 * Split from use-supabase.ts per #566.
 */
import { getSupabase } from "@/lib/supabase";
import { createLogger } from '@/lib/logger';
import { useSupabaseQuery, type UseQueryResult } from './use-supabase-core';

const log = createLogger('Search');

// ── Search ───────────────────────────────────────────

export interface SearchResultItem {
  id: string;
  type: "topic" | "atom" | "user";
  title: string;
  subtitle?: string;
  href: string;
}

/**
 * Sanitize user input for PostgREST ilike filters.
 * Escapes wildcards that could break .or() filter syntax.
 * [SYNC] Canonical source: workers/extract/src/shared.ts#sanitizeForIlike (#569)
 */
export function sanitizeForIlike(input: string): string {
  // #538: Align with workers/extract/src/shared.ts — escape wildcards instead of stripping
  return input.replace(/[%_\\]/g, '\\$&');
}

// #285: Race conditions are handled by useSupabaseQuery's fetchIdRef (stale results discarded).
// AbortController not used because Supabase JS client doesn't support abort signals natively.
export function useSearch(query: string): UseQueryResult<SearchResultItem[]> {
  const trimmed = query.trim().toLowerCase();
  return useSupabaseQuery(
    trimmed.length >= 1
      ? async () => {
          const sb = getSupabase();
          const results: SearchResultItem[] = [];
          const safe = sanitizeForIlike(trimmed);
          if (!safe) return results; // nothing left after sanitization

          // #563: Fire all 3 search queries in parallel instead of sequential
          // Atom search: try tsvector first (fast, indexed), fallback to ilike
          const [topicsRes, atomsTsvRes, profilesRes] = await Promise.all([
            sb.from("topics")
              .select("id, title, description")
              .or(`title.ilike.%${safe}%,description.ilike.%${safe}%`)
              .limit(8),
            sb.from("atom_feed_view")
              .select("id, concern, summary, value_axis, visibility, author_name, topic_id")
              .textSearch("search_tsv", safe, { type: "plain" })
              .neq("visibility", "anonymous")
              .limit(8),
            sb.from("profiles")
              .select("user_id, display_name, bio")
              .or(`display_name.ilike.%${safe}%,bio.ilike.%${safe}%`)
              .limit(5),
          ]);

          // If tsvector returned nothing (CJK tokenization miss), fallback to ilike on view
          let atomsData = atomsTsvRes.data;
          if ((!atomsData || atomsData.length === 0) && !atomsTsvRes.error) {
            log.info('tsvector miss, fallback to ilike', { query: safe });
            const fallback = await sb.from("atom_feed_view")
              .select("id, concern, summary, value_axis, visibility, author_name, topic_id")
              .or(`concern.ilike.%${safe}%,summary.ilike.%${safe}%,topic.ilike.%${safe}%`)
              .neq("visibility", "anonymous")
              .limit(8);
            if (!fallback.error) atomsData = fallback.data;
          }

          // Assemble results from parallel responses
          if (!topicsRes.error && topicsRes.data) {
            for (const t of topicsRes.data) {
              results.push({
                id: t.id,
                type: "topic",
                title: t.title,
                subtitle: t.description?.slice(0, 40) ?? '',
                href: `/topic/${t.id}`,
              });
            }
          }

          if (atomsData) {
            for (const a of atomsData as Array<Record<string, unknown>>) {
              results.push({
                id: a.id as string,
                type: "atom",
                title: (a.concern as string) ?? "无标题",
                subtitle: [a.author_name, a.value_axis].filter(Boolean).join(' · '),
                href: `/talk/${a.id}`,
              });
            }
          }

          if (!profilesRes.error && profilesRes.data) {
            for (const p of profilesRes.data) {
              results.push({
                id: p.user_id,
                type: "user",
                title: p.display_name ?? p.user_id.slice(0, 8),
                subtitle: p.bio?.slice(0, 40) ?? "",
                href: `/profile/${p.user_id}`,
              });
            }
          }

          return results;
        }
      : null,
    [trimmed],
  );
}
